import React, { useRef, useEffect } from 'react';
import { Clock } from 'lucide-react';
import PropTypes from 'prop-types';
import {
  formatSearchLabel,
  clearRecentSearches
} from '../../../utils/recentSearches';

const RecentSearchesDropdown = ({
  recentSearches = [],
  onSelectSearch,
  onClearSearches,
  className = '',
  isOpen = true,
  onClose
}) => {
  const dropdownRef = useRef(null);
  const contentRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  const handleSelectSearch = (search) => {
    onSelectSearch(search);
  };

  const handleClearAll = () => {
    clearRecentSearches();
    onClearSearches();
  };

  if (recentSearches.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className="w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50"
        style={{ width: '24rem' }}
        ref={contentRef}
      >
        <div className="max-h-80 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Recent searches
              </span>
            </div>
            <button
              onClick={handleClearAll}
              className="text-xs text-gray-500 hover:text-red-600 transition-colors"
            >
              Clear all
            </button>
          </div>

          {/* Recent search items */}
          <div className="py-1">
            {recentSearches.map((search) => (
              <button
                key={search.id}
                onClick={() => handleSelectSearch(search)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 font-medium truncate">
                      {formatSearchLabel(search.searchQuery, search.filters)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(search.timestamp).toLocaleDateString()} at{' '}
                      {new Date(search.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

RecentSearchesDropdown.propTypes = {
  recentSearches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      searchQuery: PropTypes.string,
      filters: PropTypes.object.isRequired,
      timestamp: PropTypes.string.isRequired
    })
  ),
  onSelectSearch: PropTypes.func.isRequired,
  onClearSearches: PropTypes.func.isRequired,
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

export default RecentSearchesDropdown;
